import React, {useEffect, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import Layout from "../../Layout";
import assessmentAPI from '../../services/AssessmentAPI';

import {
    AlertCircle,
    ArrowLeft,
    BookOpen,
    CheckCircle,
    Clock,
    Eye,
    FileText,
    GraduationCap,
    Loader,
    Play,
    Upload,
    User,
    X
} from 'lucide-react';

const AssessmentDetails = () => {
    const { assessmentId } = useParams();
    const navigate = useNavigate();

    // State management
    const [assessment, setAssessment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState(null);

    // Fetch assessment details on component mount
    useEffect(() => {
        fetchAssessmentDetails();
    }, [assessmentId]);

    // Fetch assessment details
    const fetchAssessmentDetails = async () => {
        try {
            setLoading(true);
            const assessmentData = await assessmentAPI.getAssessmentDetails(assessmentId);
            setAssessment(assessmentData);
        } catch (err) {
            console.error("Error fetching assessment:", err);
            setError("Failed to load assessment details");
        } finally {
            setLoading(false);
        }
    };

    // Navigation functions
    const goBack = () => {
        navigate('/assessments');
    };

    const startAssessment = () => {
        navigate(`/assessments/${assessmentId}/marks-entry`);
    };

    const viewSubmission = () => {
        navigate(`/assessments/${assessmentId}/submission`);
    };

    // Utility functions
    const showNotification = (type, message) => {
        setNotification({type, message});
        setTimeout(() => setNotification(null), 5000);
    };

    // Get status badge
    const getStatusBadge = () => {
        if (!assessment?.existing_marks) {
            return (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Not Started
                </span>
            );
        }

        const statusDisplay = assessmentAPI.getSubmissionStatusDisplay(assessment.existing_marks.status);
        const colorClasses = {
            gray: 'bg-gray-100 text-gray-800',
            yellow: 'bg-yellow-100 text-yellow-800',
            blue: 'bg-blue-100 text-blue-800',
            green: 'bg-green-100 text-green-800',
            orange: 'bg-orange-100 text-orange-800'
        };

        return (
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${colorClasses[statusDisplay.color]}`}>
                {statusDisplay.text}
            </span>
        );
    };

    // Get action button
    const getActionButton = () => {
        if (!assessment?.existing_marks) {
            return (
                <button
                    onClick={startAssessment}
                    className="flex items-center gap-2 px-6 py-3 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors font-medium"
                >
                    <Play className="w-5 h-5"/>
                    Start Assessment
                </button>
            );
        }

        const status = assessment.existing_marks.status;

        if (status === 'submitted' || status === 'approved') {
            return (
                <button
                    onClick={viewSubmission}
                    className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                >
                    <Eye className="w-5 h-5"/>
                    View Submission
                </button>
            );
        }

        if (status === 'revision_requested') {
            return (
                <button
                    onClick={startAssessment}
                    className="flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
                >
                    <Upload className="w-5 h-5"/>
                    Revise Submission
                </button>
            );
        }

        return (
            <button
                onClick={startAssessment}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
                <Upload className="w-5 h-5"/>
                Continue Assessment
            </button>
        );
    };

    if (loading) {
        return (
            <Layout>
                <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                    <div className="flex justify-center items-center h-64">
                        <Loader className="w-8 h-8 text-[#D67C2C] animate-spin"/>
                        <span className="ml-3 text-lg text-gray-600">Loading assessment...</span>
                    </div>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                    <div className="flex justify-center items-center h-64 text-red-500">
                        <AlertCircle className="w-6 h-6 mr-2"/>
                        <span>{error}</span>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Notification */}
                    {notification && (
                        <div
                            className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 ${
                                notification.type === 'success' ? 'bg-green-500 text-white' :
                                    notification.type === 'error' ? 'bg-red-500 text-white' :
                                        'bg-blue-500 text-white'
                            }`} style={{animation: 'slideInRight 0.5s ease-out'}}>
                            {notification.type === 'success' ? <CheckCircle className="w-5 h-5"/> :
                                notification.type === 'error' ? <AlertCircle className="w-5 h-5"/> :
                                    <AlertCircle className="w-5 h-5"/>}
                            <span className="font-[poppins]">{notification.message}</span>
                            <button onClick={() => setNotification(null)} className="ml-2 hover:opacity-80">
                                <X className="w-4 h-4"/>
                            </button>
                        </div>
                    )}

                    {/* Header */}
                    <div className="pt-8 pb-8 text-center relative overflow-hidden">
                        <div className="relative z-10">
                            <button
                                onClick={goBack}
                                className="absolute left-0 top-0 flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-[#D67C2C] transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5"/>
                                Back to Assessments
                            </button>
                            
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                                 style={{backgroundColor: '#D67C2C'}}>
                                <FileText className="w-8 h-8 text-white"/>
                            </div>
                            <h1 className="text-5xl font-bold bg-[#D67C2C] bg-clip-text text-transparent mb-4">
                                Assessment Details
                            </h1>
                            <p className="text-[#1D3469] text-xl font-medium max-w-3xl mx-auto px-4">
                                Review the assessment information before starting
                            </p>
                        </div>
                    </div>

                    {/* Assessment Information */}
                    <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
                        <div className="p-6 border-b border-gray-100 bg-gray-50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                                        <FileText className="w-5 h-5 text-white"/>
                                    </div>
                                    <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">
                                        {assessment?.setup?.title || 'Assessment'}
                                    </h2>
                                </div>
                                {getStatusBadge()}
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div className="space-y-4">
                                    <div className="flex items-center gap-3">
                                        <BookOpen className="w-5 h-5 text-blue-600"/>
                                        <div>
                                            <p className="text-sm text-gray-500">Subject/Domain</p>
                                            <p className="font-medium text-gray-900">
                                                {assessment?.setup?.subject || assessment?.setup?.domain || 'N/A'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <GraduationCap className="w-5 h-5 text-green-600"/>
                                        <div>
                                            <p className="text-sm text-gray-500">Grade</p>
                                            <p className="font-medium text-gray-900">
                                                Grade {assessment?.setup?.grade || 'N/A'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <Clock className="w-5 h-5 text-orange-600"/>
                                        <div>
                                            <p className="text-sm text-gray-500">Duration</p>
                                            <p className="font-medium text-gray-900">
                                                {assessment?.setup?.duration ? `${assessment.setup.duration} minutes` : 'No time limit'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-center gap-3">
                                        <FileText className="w-5 h-5 text-purple-600"/>
                                        <div>
                                            <p className="text-sm text-gray-500">Total Questions</p>
                                            <p className="font-medium text-gray-900">
                                                {assessment?.questions?.length || 0} questions
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <CheckCircle className="w-5 h-5 text-indigo-600"/>
                                        <div>
                                            <p className="text-sm text-gray-500">Total Marks</p>
                                            <p className="font-medium text-gray-900">
                                                {assessment?.setup?.total_marks || 
                                                 assessment?.questions?.reduce((sum, q) => sum + (q.marks || 0), 0) || 0} marks
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <User className="w-5 h-5 text-pink-600"/>
                                        <div>
                                            <p className="text-sm text-gray-500">Assessment Type</p>
                                            <p className="font-medium text-gray-900 capitalize">
                                                {assessment?.setup?.assessment_type || 'Test'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Instructions */}
                            {assessment?.content?.instructions && (
                                <div className="bg-blue-50 rounded-lg p-6 border border-blue-200 mb-6">
                                    <h3 className="text-lg font-semibold text-blue-900 mb-3">Instructions</h3>
                                    <div 
                                        className="text-blue-800 prose prose-sm max-w-none"
                                        dangerouslySetInnerHTML={{__html: assessment.content.instructions}}
                                    />
                                </div>
                            )}

                            {/* Questions Overview */}
                            {assessment?.questions && assessment.questions.length > 0 && (
                                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 mb-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Questions Overview</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {assessment.questions.map((question, index) => (
                                            <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-sm font-medium text-gray-600">
                                                        Question {index + 1}
                                                    </span>
                                                    <span className="text-sm font-semibold text-[#D67C2C]">
                                                        {question.marks || 1} mark{(question.marks || 1) > 1 ? 's' : ''}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-gray-700 capitalize">
                                                    {question.type?.replace('_', ' ') || 'Question'}
                                                </p>
                                                {question.difficulty && (
                                                    <span className={`inline-block mt-2 px-2 py-1 rounded-full text-xs font-medium ${
                                                        question.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                                                        question.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-red-100 text-red-800'
                                                    }`}>
                                                        {question.difficulty}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Submission Status */}
                            {assessment?.existing_marks && (
                                <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200 mb-6">
                                    <h3 className="text-lg font-semibold text-yellow-900 mb-3">Submission Status</h3>
                                    <div className="space-y-2 text-yellow-800">
                                        <p><strong>Status:</strong> {assessmentAPI.getSubmissionStatusDisplay(assessment.existing_marks.status).text}</p>
                                        {assessment.existing_marks.total_marks !== undefined && (
                                            <p><strong>Your Score:</strong> {assessment.existing_marks.total_marks}/{assessment.existing_marks.max_total_marks} ({assessment.existing_marks.percentage}%)</p>
                                        )}
                                        {assessment.existing_marks.submitted_at && (
                                            <p><strong>Submitted:</strong> {assessmentAPI.formatDate(assessment.existing_marks.submitted_at)}</p>
                                        )}
                                        {assessment.existing_marks.teacher_feedback && (
                                            <div className="mt-3">
                                                <p><strong>Teacher Feedback:</strong></p>
                                                <p className="mt-1 italic">{assessment.existing_marks.teacher_feedback}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Action Button */}
                            <div className="flex justify-center">
                                {getActionButton()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default AssessmentDetails;